// SlimAuthedLayout — the shell for authed pages that are NOT the full app (the
// project chooser + the terminal pages). Same ProtectedRoute gate + skip-link +
// single <main> as AuthedLayout, but the bar drops the operational tools (search,
// notifications, help, gear, domains) — they have nothing to act on here.
//
// Two slots, set per route:
//   brand: 'lockup'   — the RubixEMS lockup (cube + wordmark), brand-only. Used
//                       where the page itself is the navigation (/select-project,
//                       /no-projects).
//          'switcher' — cube (home link) + ProjectSwitcher, so the page stays
//                       aligned with a project page and offers a direct way out.
//                       Used by /project-blocked (the switcher is the escape).
//   account: 'full'   — the normal avatar (profile / password reachable).
//            'minimal' — trimmed avatar (identity + theme + language + sign out;
//                        no profile / change password). Used on the true dead-end
//                        (/no-projects).
//
// Sign out lives in the avatar on EVERY variant → there is always an exit (no
// authed page can hard-lock). A sysadmin's gear sits beside the avatar at every
// width (`Gear` — the platform door; these pages have no hamburger drawer to
// carry it), so the project chooser reaches Platform settings, where projects
// are created.

import { useRef } from 'react';
import { Outlet, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogoMark, LogoLockup } from '../ui/Logo.js';
import { ProjectSwitcher } from './ProjectSwitcher.js';
import { AccountMenu } from './AccountMenu.js';
import { Gear } from './Gear.js';
import { ProtectedRoute } from '../auth/ProtectedRoute.js';
import { ShellTop } from './ShellTop.js';
import { useFocusMainOnNavigate } from '../../hooks/useFocusMainOnNavigate.js';
import { pageBox, pageGutter } from './pageBox.js';

export function SlimAuthedLayout({
  brand,
  account,
}: {
  brand: 'lockup' | 'switcher';
  account: 'full' | 'minimal';
}) {
  const { t } = useTranslation('nav');
  // The blocked page carries the project it's about (`?project=`); naming it in
  // the switcher keeps the bar coherent with an in-project page.
  const [params] = useSearchParams();
  const contextProjectId = params.get('project');
  // SPA route-change focus → move focus to <main> on navigation (a11y).
  const mainRef = useRef<HTMLElement>(null);
  useFocusMainOnNavigate(mainRef);
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-canvas)] text-[var(--color-text-primary)]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:start-2 focus:z-tooltip focus:px-3 focus:py-2 focus:rounded-md focus:bg-[var(--color-bg-page)] focus:text-[var(--color-text-primary)] focus:border focus:border-[var(--color-border)] focus:shadow-overlay focus:outline-2 focus:outline-offset-2 focus:outline-[var(--color-primary-ring)]"
      >
        {t('skipToContent')}
      </a>
      {/* The frame's pinned top (`ShellTop` — every frame measures itself; this
          project-less one carries no project strips). Band caps to the shared
          pageBox (boxed app frame); inner content gutter. */}
      <ShellTop>
        <header className={`${pageBox} h-14 border-b border-[var(--color-border)] bg-[var(--color-chrome)]`}>
          <div className={`flex h-full items-center gap-2 ${pageGutter}`}>
            {brand === 'switcher' ? (
              <>
                {/* Aligned with a project page: cube = brand/home, then the context
                    control (project-less here → "Switch project" + dropdown to escape). */}
                <RouterLink
                  to="/"
                  aria-label={t('homeLink')}
                  className="group flex items-center shrink-0 rounded-sm outline-none focus-ring"
                >
                  <LogoMark size={28} aria-hidden className="text-[var(--color-text-primary)]" />
                </RouterLink>
                <ProjectSwitcher projectId={contextProjectId} />
              </>
            ) : (
              <LogoLockup height={26} className="text-[var(--color-text-primary)]" />
            )}
            <div className="ms-auto shrink-0 flex items-center gap-1 sm:gap-2">
              <Gear projectId={null} />
              <AccountMenu projectId={null} minimal={account === 'minimal'} />
            </div>
          </div>
        </header>
      </ShellTop>
      <ProtectedRoute>
        <main ref={mainRef} id="main-content" tabIndex={-1} className="flex-1 flex flex-col min-w-0 outline-none">
          <Outlet />
        </main>
      </ProtectedRoute>
    </div>
  );
}
